import { recordSyncedSnowplowUserId } from '../integrations/snowplow-sdk/capturedUserId';
import { User } from '@/api/types/public';
import { getZeeloolSnowplowClient } from '@/reporting/integrations/snowplow-sdk/client';
import { getPageReferSpm } from '@/reporting/spm/spm-session';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { env } from '@/utils/env';
import { getPlatformLanguage } from '@/utils/locale-cookie';
import { isInZeloolApp } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { markSensorsInitialized } from '../reliability/identityGate';
import { loadAnalyticsScript } from '../reliability/scriptLoader';
import { readLiveSensorsAnonymousId, syncSensorsIdentityToStorage } from '../utils/sensorsIdentity';
import {
  installManualAnalytics,
  registerManualLogin,
  registerManualOperations,
  registerManualTrack,
} from '../reliability/manualAnalyticsBridge';

/** 神策初始化所需的显式配置。 */
interface SensorsConfig {
  serverUrl: string;
  showLog: boolean;
  platform: string;
  platformName: string;
  envName: string;
  isEnabled?: boolean;
}

/** 页面来源写入仅依赖 SDK 的公共属性接口，初始化回调不依赖 Provider 的就绪标记。 */
interface SensorsPageRegistry {
  registerPage?: (data: Record<string, unknown>) => void;
  clearPageRegister?: (keys: string[]) => void;
}

function registerSensorsSpmProperty(
  sensors: SensorsPageRegistry | undefined,
  spm: string | undefined,
): void {
  if (typeof sensors?.registerPage !== 'function') return;
  if (spm) {
    sensors.registerPage({ spm });
  } else {
    sensors.clearPageRegister?.(['spm']);
  }
}

/** 复用神策既有公共字段计算；配置字段保持初始化时的值。 */
function createSensorsCommonProperties(config?: SensorsConfig): Record<string, unknown> {
  const params = {
    // 身份字段不会自动成为可查询的事件属性；每次发送时与 SDK 当前身份保持一致。
    anonymous_id: isInZeloolApp() ? undefined : () => readLiveSensorsAnonymousId(),
    pub_platform: config?.platform,
    pub_platform_name: config?.platformName,
    pub_platform_language: () => getPlatformLanguage(),
    pub_release_time: () => {
      try {
        const currentWindow = globalThis.window as
          | (Window & { __ZEELOOL_BUILD__META?: { release_time?: unknown } })
          | undefined;
        const value = currentWindow?.__ZEELOOL_BUILD__META?.release_time;
        return typeof value === 'string' ? value.trim() || undefined : undefined;
      } catch {
        return undefined;
      }
    },
    pub_client: config?.platform,
    pub_env_name: () => {
      // 区分灰度的新服务还是旧服务
      if (config?.envName === 'production') return 'production_is_nextjs';
      return config?.envName;
    },
    pub_member_level: () => {
      let userInfo: User = {};
      try {
        userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
      } catch (error) {
        logger.error('[SensorsProvider] public_property_unavailable');
      }
      return `${userInfo.member_level >= -1 ? userInfo.member_level : '9999'}`;
    },
    pub_is_paid: () => {
      let userInfo: User = {};
      try {
        userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
      } catch (error) {
        logger.error('[SensorsProvider] public_property_unavailable');
      }
      return !!userInfo.last_pay_time;
    },
    pub_is_login: () => {
      try {
        return !!localStorage.getItem('zl-token');
      } catch (error) {
        logger.error('[SensorsProvider] public_property_unavailable');
        return false;
      }
    },
    pub_zl_utm_source: () => {
      if (window.latestChannelAttribute?.expires > Date.now()) {
        return window.latestChannelAttribute?.pub_zl_utm_source || undefined;
      }
      return undefined;
    },
    pub_zl_utm_medium: () => {
      if (window.latestChannelAttribute?.expires > Date.now()) {
        return window.latestChannelAttribute?.pub_zl_utm_medium || undefined;
      }
      return undefined;
    },
    pub_zl_utm_campaign: () => {
      if (window.latestChannelAttribute?.expires > Date.now()) {
        return window.latestChannelAttribute?.pub_zl_utm_campaign || undefined;
      }
      return undefined;
    },
    pub_zl_utm_campaignid: () => {
      if (window.latestChannelAttribute?.expires > Date.now()) {
        return window.latestChannelAttribute?.pub_zl_utm_campaignid || undefined;
      }
      return undefined;
    },
    pub_zl_referrer: () => {
      if (window.latestChannelAttribute?.expires > Date.now()) {
        return window.latestChannelAttribute?.pub_zl_referrer || undefined;
      }
      return undefined;
    },
    pub_zl_page: () => {
      if (
        window.latestChannelAttribute?.expires > Date.now() ||
        window.latestChannelAttribute?.expires === -1
      ) {
        return window.latestChannelAttribute?.pub_zl_page || undefined;
      }
      return undefined;
    },
    pub_landing_page: () => {
      return window.latestChannelAttribute?.pub_landing_page || undefined;
    },
  };
  return params;
}

/** 触发时物化动态公共属性，重放不读取未来用户、归因和URL。 */
export function captureSensorsEventProperties(): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    $url: window.location.href,
    // null 明确保留无来源，跨文档 JSON 持久化也不能丢失这个状态。
    spm: getPageReferSpm() ?? null,
  };
  for (const [key, value] of Object.entries(createSensorsCommonProperties())) {
    // 匿名身份随 SDK 构包时求值，初始化前的空快照不能遮蔽首次建立的身份。
    if (key === 'anonymous_id') continue;
    if (key === 'pub_env_name' || typeof value !== 'function') continue;
    try {
      properties[key] = value();
    } catch {
      // 单字段读取失败也保留空快照，不能污染或阻断另一渠道。
      properties[key] = undefined;
    }
  }
  return properties;
}

/** 神策先合并后求值；共享空值函数遮蔽未来公共属性并由SDK最终移除。 */
function omitCapturedPublicProperty(): undefined {
  return undefined;
}

/** 将可选 SDK user ID 规范化为空值安全的字符串。 */
function normalizeSnowplowUserId(userId?: string): string | null {
  // nullish 值不应被猜测为 Sensors uuid。
  if (userId === null || userId === undefined) return null;

  const normalizedUserId = String(userId).trim();
  const lowerCaseUserId = normalizedUserId.toLowerCase();

  // 空字符串与字符串 nullish 值都不能进入 SDK identity。
  if (!normalizedUserId || lowerCaseUserId === 'null' || lowerCaseUserId === 'undefined') {
    return null;
  }

  return normalizedUserId;
}

/** 同一窗口共享初始化Promise和成功步骤，失败重试不重复注册自动采集。 */
interface SensorsInitialization {
  promise?: Promise<boolean>;
  completedSteps: Set<'init' | 'properties' | 'pageLeave' | 'autoTrack'>;
}

/** 多个Provider实例共享当前窗口的一次初始化。 */
const initializations = new WeakMap<Window, SensorsInitialization>();

/** 神策上报 Provider，并负责登录后同步 SDK 用户 ID。 */
export class SensorsProvider implements Provider {
  /** ReportingCenter 使用的 Provider 类型。 */
  type = ProviderType.SENSORS;

  /** 当前 Provider 是否启用。 */
  isEnabled: boolean;

  /** 是否输出神策调试日志。 */
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问

  /** 神策 SDK 是否已经完成初始化。 */
  private initialized = false;

  /** 当前实例使用的神策配置。 */
  private config: SensorsConfig;

  /** 保存显式配置并解析本地日志开关。 */
  constructor(config: SensorsConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  /** 初始化神策 SDK、公共属性、PageLeave 插件与身份快照。 */
  async init(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    if (this.initialized) return true;
    let state = initializations.get(window);
    if (!state) {
      state = { completedSteps: new Set() };
      initializations.set(window, state);
    }
    // 延后执行实际初始化，先登记Promise以覆盖同步SDK和多个并发调用。
    state.promise ??= Promise.resolve().then(() => this.initializeSensors(state));
    const pending = state.promise;
    const initialized = await pending;
    if (!initialized && state.promise === pending) state.promise = undefined;
    this.initialized = initialized;
    return initialized;
  }

  /** 按成功步骤续跑初始化；脚本pending超过编排等待窗口仍继续共享。 */
  private async initializeSensors(state: SensorsInitialization): Promise<boolean> {
    try {
      // 动态加载神策脚本（如果尚未加载）
      if (!window.sensorsDataAnalytic201505) {
        await this.loadSensorsScript();
      }

      // Get the instance if it's already loaded
      const sensors = window.sensorsDataAnalytic201505;
      window.sensors = sensors;
      if (
        !sensors ||
        ['init', 'registerPage', 'use', 'quick'].some(
          (method) => typeof sensors[method] !== 'function',
        )
      ) {
        logger.error('[SensorsProvider] sensors_sdk_invalid');
        return false;
      }

      // 已成功的SDK init不能因后续插件失败再执行。
      if (!isInZeloolApp()) installManualAnalytics(sensors);
      if (!state.completedSteps.has('init')) {
        sensors.init({
          server_url: this.config.serverUrl,
          // App H5 不覆盖收集地址；普通网页同域收集沿用当前环境的神策项目。
          custom_server_url: isInZeloolApp()
            ? undefined
            : window.location.origin +
              '/vgmkt-metrics/sd/collect?project=' +
              encodeURIComponent(new URL(this.config.serverUrl).searchParams.get('project') ?? ''),
          app_js_bridge: isInZeloolApp(),
          // SDK 在 history 变更后、构造自动 $pageview 前同步执行，不能等 React effect。
          is_track_single_page: () => {
            try {
              registerSensorsSpmProperty(sensors, getPageReferSpm());
            } catch {
              logger.error('[SensorsProvider] refresh_spm_page_property_failed');
            }
            return true;
          },
          use_client_time: true,
          send_type: 'ajax',
          heatmap: {
            // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
            clickmap: 'default',
            // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
            scroll_notice_map: 'default',
          },
          show_log: this.config.showLog,
          batch_send: {
            datasend_timeout: 6000, //一次请求超过多少毫秒的话自动取消，防止请求无响应。
            send_interval: 800, //间隔多少毫秒发一次数据。
            storage_length: 1000, // 存储 localStorage 条数最大值，默认：1000 。如 localStorage 条数超过该值，则使用 image 方式立即发送数据。v1.24.8 以上支持。
          },
        });
        state.completedSteps.add('init');
      }

      // 每一步只有成功后才记录；重试只恢复尚未完成的步骤。
      if (!state.completedSteps.has('properties')) {
        this.registerCommonProperties(sensors);
        state.completedSteps.add('properties');
      }
      if (!state.completedSteps.has('pageLeave')) {
        sensors.use('PageLeave', {});
        state.completedSteps.add('pageLeave');
      }
      if (!state.completedSteps.has('autoTrack')) {
        sensors.quick('autoTrack');
        state.completedSteps.add('autoTrack');
      }
      this.syncIdentity();
      markSensorsInitialized();
      return true;
    } catch (error) {
      logger.error('[SensorsProvider] sensors_initialization_failed', error);
      return false;
    }
  }

  /** 把普通业务事件转换为神策 payload 并隔离 SDK 异常。 */
  track(event: TrackingEvent): void {
    if (typeof window !== 'undefined' && !isInZeloolApp()) {
      if (this.isEnabled) registerManualTrack(event, 'z.SensorsProvider.track');
      return;
    }
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      const sensors = window.sensorsDataAnalytic201505;
      try {
        const _params: Record<string, any> = {
          $url: window.location.href,
        };
        // 根据标准：只要是值为 '', null, undefined, 'null', 'undefined'
        Object.keys(event.properties).forEach((key) => {
          const v = event.properties[key];
          if (
            v !== '' &&
            v !== 'null' &&
            v !== 'undefined' &&
            v !== null &&
            v !== undefined &&
            !(v instanceof Array && !v.length)
          ) {
            _params[key] = v;
          }
        });
        if (
          event.options &&
          Object.prototype.hasOwnProperty.call(event.options, 'spmReferSnapshot')
        ) {
          const frozenRefer: unknown = Object.prototype.hasOwnProperty.call(event.properties, 'spm')
            ? event.properties.spm
            : event.options.spmReferSnapshot;
          _params.spm = frozenRefer ?? omitCapturedPublicProperty;
        }
        // 神策自动上报链路禁止 event_id，使用 sensors_event_id 承载内部统一事件 ID。
        if (event.eventId) {
          _params.sensors_event_id = event.eventId;
        }
        if (this.loggingEnabled) {
          logger.log('sensorProvider, Event:', event.eventName, _params);
        }
        sensors.track(event.eventName, _params);
      } catch (error) {
        logger.log('sensors track error', error);
      }
    } catch (error) {
      logger.error('Sensors tracking error:', error);
    }
  }

  /** 浏览器可靠交付仅消费冻结属性；track开始后异常为uncertain。 */
  deliverCaptured(event: TrackingEvent): import('../reliability/deliveryQueue').ChannelResult {
    if (!this.initialized || typeof window === 'undefined') return 'pending';
    const sensors = window.sensorsDataAnalytic201505;
    if (typeof sensors?.track !== 'function') return 'pending';
    const properties: Record<string, unknown> = { ...event.properties };
    // 无来源快照（含旧记录缺少字段）遮蔽当前页面公共 spm；SDK 求值后移除该字段。
    if (properties.spm === null || properties.spm === undefined) {
      properties.spm = omitCapturedPublicProperty;
    }
    // 仅转换既有公共字段的自有undefined；冻结快照与业务属性保持原样。
    for (const key of Object.keys(createSensorsCommonProperties(this.config))) {
      if (Object.prototype.hasOwnProperty.call(properties, key) && properties[key] === undefined) {
        properties[key] = omitCapturedPublicProperty;
      }
    }
    if (event.eventId) properties.sensors_event_id = event.eventId;
    try {
      sensors.track(event.eventName, properties);
      return 'handed-off';
    } catch {
      logger.error('[SensorsProvider] captured_track_uncertain');
      return 'uncertain';
    }
  }

  /**
   * 登出时仅更新登录状态画像，保留神策身份与 Snowplow uid。
   * 业务需求：用户退出或切换账号后可能不再登录，此期间所有行为仍归属退出前账号。
   * 无入参；不得在此调用 logout 重置身份或清空已同步的 Snowplow user ID。
   * @returns 无返回值。
   */
  sensorsLogout(): void {
    if (typeof window !== 'undefined' && !isInZeloolApp()) {
      registerManualOperations('z.SensorsProvider.sensorsLogout', [
        { kind: 'profile', properties: { pub_is_login: false } },
      ]);
      return;
    }
    try {
      // window.sensorsDataAnalytic201505.logout();
      window.sensorsDataAnalytic201505.setProfile({ pub_is_login: false });
    } catch (error) {
      logger.log('sensors logout error', error);
    }
  }

  /**
   * 调用神策登录，回读确认身份绑定成功后再同步显式 SDK user ID。
   */
  sensorsLogin(uuid: string, snowplowUserId?: string): boolean {
    try {
      const normalizedUuid = uuid.trim();
      if (!normalizedUuid) return false;

      if (typeof window !== 'undefined' && !isInZeloolApp()) {
        const alreadyBound =
          window.sensorsDataAnalytic201505?.store?.getDistinctId?.() === normalizedUuid;
        registerManualLogin(
          'z.SensorsProvider.sensorsLogin',
          normalizedUuid,
          normalizeSnowplowUserId(snowplowUserId) ?? undefined,
          () => {
            this.syncIdentity();
            this.syncSnowplowUserId(snowplowUserId);
          },
        );
        return alreadyBound;
      }

      window.sensorsDataAnalytic201505.login(normalizedUuid);
      const identity = this.syncIdentity();
      if (identity.distinctId !== normalizedUuid) return false;

      this.syncSnowplowUserId(snowplowUserId);
      return true;
    } catch (error) {
      logger.log('sensors login error', error);
      return false;
    }
  }

  /** 返回神策 Provider 初始化状态。 */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 仅把显式 customer id 同步给 SDK，失败不改变神策登录成功结果。
   */
  private syncSnowplowUserId(userId?: string): void {
    const normalizedUserId = normalizeSnowplowUserId(userId);

    // 支付静默链只传 uuid 时不得猜测或设置 SDK user ID。
    if (!normalizedUserId) return;

    try {
      const client = getZeeloolSnowplowClient();
      if (!client) return;
      client.setUserId(normalizedUserId);
      recordSyncedSnowplowUserId(client, normalizedUserId);
    } catch {
      // 日志只记录稳定错误类别，不携带 SDK 异常或身份值。
      logger.error('[SensorsProvider] snowplow_set_user_id_failed');
    }
  }

  /**
   * 将神策 SDK 当前身份同步到本地缓存，供 Snowplow 首屏事件兜底读取。
   */
  private syncIdentity(): ReturnType<typeof syncSensorsIdentityToStorage> {
    return syncSensorsIdentityToStorage();
  }

  /**
   * 动态加载神策脚本
   */
  private loadSensorsScript(): Promise<void> {
    return loadAnalyticsScript('sensors');
  }

  /** 注册神策按事件实时读取的公共属性。 */
  private registerCommonProperties(sensors: any): void {
    // const latestChannelAttribute = this.getLatestChannelAttribute();
    const params = createSensorsCommonProperties(this.config);
    for (const key in params) {
      // 虽说不再报警告了但是值失去了实时性
      // if (typeof params[key] === 'function') {
      //   params[key] = params[key]();
      // }
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    }
    // 站内来源 spm 注册到页面公共属性：内置全埋点事件（$WebClick/$WebPageLoad 等）
    // 也携带来源，与神策 $latest_* 渠道归因互补；路由变化由 refreshSpmPageProperty 刷新。
    const spmRefer = getPageReferSpm();
    if (spmRefer) {
      params.spm = spmRefer;
    }
    sensors.registerPage(params);
  }

  /**
   * 路由变化时刷新页面公共属性中的站内来源 spm（spm 注册到全局，内置事件也带）。
   * registerPage 为合并语义：有来源合并写入；无来源用 clearPageRegister 清除，
   * 避免"仅第一页有效"的来源串到后续页面。未初始化或 SDK 缺失时静默跳过。
   */
  refreshSpmPageProperty(spm: string | undefined): void {
    if (!this.initialized || typeof window === 'undefined') return;
    try {
      registerSensorsSpmProperty(window.sensorsDataAnalytic201505, spm);
    } catch {
      logger.error('[SensorsProvider] refresh_spm_page_property_failed');
    }
  }

  /**
   * 启用Sensors日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Sensors日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }
}
